import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Box, Paper, Typography, InputBase, IconButton, CircularProgress } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import LocationOnIcon from '@mui/icons-material/LocationOn'

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN

function MapSearchBar({ mapRef, isDark, placementMode, onPlaceNode }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const debounceRef = useRef(null)
  const inputRef = useRef(null)
  const wrapperRef = useRef(null)

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleInputChange = useCallback((e) => {
    const value = e.target.value
    setQuery(value)
    setSelectedIndex(-1)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.trim().length < 2) {
      setResults([])
      setIsOpen(false)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        // Get current map center for proximity bias
        const center = mapRef.current?.getCenter?.()
        const proximity = center ? `&proximity=${center.lng},${center.lat}` : ''
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value.trim())}.json?access_token=${MAPBOX_TOKEN}&types=poi,address,place,locality,neighborhood&limit=8&language=en${proximity}&fuzzyMatch=true`
        )
        const data = await response.json()
        setResults(data.features || [])
        setIsOpen(true)
      } catch (err) {
        console.error('Geocoding error:', err)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, 300)
  }, [])

  const handleSelect = useCallback((feature) => {
    const [lng, lat] = feature.center
    setQuery(feature.place_name)
    setIsOpen(false)
    setResults([])

    // Zoom level based on result type: POIs/addresses get closer zoom
    const placeType = feature.place_type?.[0]
    const zoom = placeType === 'poi' ? 17
      : placeType === 'address' ? 16
      : placeType === 'neighborhood' ? 13
      : 10

    mapRef.current?.flyTo({
      center: [lng, lat],
      zoom,
      duration: 1500,
    })

    if (placementMode) {
      onPlaceNode(placementMode, lat, lng)
    }
  }, [mapRef, placementMode, onPlaceNode])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }, [results, selectedIndex, handleSelect])

  const handleClear = useCallback(() => {
    setQuery('')
    setResults([])
    setIsOpen(false)
    inputRef.current?.focus()
  }, [])

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'absolute',
        top: 12,
        left: 60,
        zIndex: 10,
        width: 280,
      }}
    >
      {/* Search Input */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          borderRadius: isOpen && results.length > 0 ? '12px 12px 0 0' : '12px',
          bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)',
        }}
      >
        <SearchIcon sx={{ fontSize: 18, color: isDark ? '#94a3b8' : '#6b7280', mr: 1 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Search locations..."
          sx={{
            flex: 1,
            fontSize: '13px',
            fontWeight: 500,
            color: isDark ? '#e2e8f0' : '#1e293b',
            '& input::placeholder': {
              color: isDark ? '#64748b' : '#9ca3af',
              opacity: 1,
            },
          }}
        />
        {isLoading && (
          <CircularProgress size={16} sx={{ color: isDark ? '#818cf8' : '#4f46e5' }} />
        )}
        {query && !isLoading && (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              width: 24,
              height: 24,
              color: isDark ? '#94a3b8' : '#6b7280',
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Paper>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '0 0 12px 12px',
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(12px)',
            borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            overflow: 'hidden',
            boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {results.map((feature, idx) => (
            <Box
              key={feature.id}
              onClick={() => handleSelect(feature)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                bgcolor: idx === selectedIndex
                  ? (isDark ? 'rgba(129,140,248,0.15)' : 'rgba(79,70,229,0.08)')
                  : 'transparent',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
                borderBottom: idx < results.length - 1
                  ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`
                  : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LocationOnIcon sx={{ fontSize: 16, color: isDark ? '#818cf8' : '#4f46e5', flexShrink: 0 }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    lineHeight: 1.3,
                  }}
                >
                  {feature.text}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: isDark ? '#64748b' : '#9ca3af',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {feature.place_name.replace(feature.text + ', ', '')}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  )
}

export default React.memo(MapSearchBar)
